import React, { useMemo } from 'react';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    ChartOptions,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import {
    PastPeriodKey,
    useDashboard,
} from '../../../../contexts/DashboardContext';
import useTranslate from '../../../../customHooks/useTranslate';
import LoadingPlaceholder from '../../../../muiComponents/LoadingPlaceholder';
import useTheme from '../../../../customHooks/useTheme';
import SectionTitle from '../../../components/SectionTitle/SectionTitle';
import { MenuItem, Select } from '@mui/material';
import {
    formatPastPeriodSubtitle,
    mostBookedColorsArray,
} from '../../../../utils/dashboard/dashbordUtils';
import useLanguage from '../../../../customHooks/useLanguage';
import { capitalize } from '../../../../utils/usefulFunctions';

// Registrar componentes de Chart.js
ChartJS.register(ArcElement, Tooltip, Legend);

const MostBookedChart = () => {
    const { hotelsBooked, changeHotelsBookedPeriod } = useDashboard();
    const language = useLanguage();
    const { getThemeColor } = useTheme();
    const labelTextColor = getThemeColor('text');
    const borderColor = getThemeColor('border');

    const translate = useTranslate();

    const data = {
        labels: hotelsBooked?.data?.map(hot => hot.HotelName) || [],
        datasets: [
            {
                data: hotelsBooked?.data?.map(hot => hot.BookingCount) || [],
                backgroundColor:
                    hotelsBooked?.data?.map(
                        (hot, ind) => mostBookedColorsArray[ind]
                    ) || [],
                borderWidth: 1,
                borderColor: borderColor,
            },
        ],
    };

    const options: ChartOptions<'doughnut'> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'right',
                labels: {
                    color: labelTextColor,
                },
            },
            tooltip: {
                callbacks: {
                    label: function (tooltipItem) {
                        const dataset =
                            tooltipItem.dataset.data[tooltipItem.dataIndex];
                        const label = tooltipItem.label || translate('No data');
                        return `${label}: ${dataset}`;
                    },
                },
            },
        },
    };

    const periodSubtitle = useMemo(() => {
        const subtitle = formatPastPeriodSubtitle(
            hotelsBooked.period.key,
            language,
            translate
        );
        return capitalize(subtitle);
    }, [hotelsBooked.period.key, language, translate]);

    return (
        <>
            <div className='flex flex-align-center flex-between'>
                <div className='flex-column gap-5'>
                    <SectionTitle
                        title={translate('most_booked_hotels')}
                        variant='h3'
                    />
                    <p className='landing-chart__subtitle'>{periodSubtitle}</p>
                </div>
                <Select
                    size='small'
                    className='landing-chart__period-select'
                    value={hotelsBooked.period.key}
                    onChange={e =>
                        changeHotelsBookedPeriod(
                            e.target.value as PastPeriodKey
                        )
                    }
                >
                    {Object.entries(PastPeriodKey).map(([key, value]) => (
                        <MenuItem key={key} value={value}>
                            {translate(value)}
                        </MenuItem>
                    ))}
                </Select>
            </div>

            {hotelsBooked.isLoading ? (
                <LoadingPlaceholder
                    variant='rounded'
                    width={'100%'}
                    height={'100%'}
                />
            ) : hotelsBooked.isDataLoaded && hotelsBooked.data.length ? (
                <div className='flex-grow flex-center-center'>
                    <Doughnut data={data} options={options} />
                </div>
            ) : (
                <div className='flex-grow flex-center-center text-size-16 font-weight-500'>
                    {translate('no_data_for_period')}
                </div>
            )}
        </>
    );
};

export default MostBookedChart;
